import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import React, { useState } from 'react';
import { getReferralDetails } from '../../api/referral.api';
import ShareReferralDrawer from '../../components/referral/ShareReferralDrawer';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Toaster } from '../../components/ui/toaster';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../../components/ui/tooltips';
import ReferralStep from './ReferralStep';

const ENTITY_ID = 'aye';

const Referral: React.FC = () => {
  const [openDrawer, setOpenDrawer] = useState(false);

  // Fetch referral details for entity 'aye'
  const { data: referralDetails, isLoading } = useQuery({
    queryKey: ['getReferralDetails', ENTITY_ID],
    queryFn: () => getReferralDetails(ENTITY_ID),
    enabled: !!ENTITY_ID
  });

  const referralLink = referralDetails?.data?.referral_link || '';
  const customMessage = referralDetails?.data?.custom_message;
  const totalSignUps = referralDetails?.data?.total_ref_count ?? 0;
  const successfulReferrals = referralDetails?.data?.successful_ref_count ?? 0;
  const isReferralActive = referralDetails?.data?.active !== false;

  const handleInviteFriend = () => {
    setOpenDrawer(true);
  };

  return (
    <>
      <div className="min-h-[80vh] p-6">
        <h3 className="mb-4 font-semibold">Refer & Earn</h3>

        <div className="mb-12 flex flex-col items-center justify-center gap-4">
          <div className="mx-auto">
            <img
              src="/assets/referral-hero.png"
              alt="referral hero"
              className="h-44"
            />
          </div>

          <p className="text-center text-sm">
            {isLoading ? (
              'Loading...'
            ) : !isReferralActive ? (
              'Referral program will be available soon.'
            ) : referralDetails?.success ? (
              <>
                Refer your friends &amp; family to earn{' '}
                <b>
                  {referralDetails?.data?.refAmount || '[amount]'}{' '}
                  {referralDetails?.data?.refRewardType || '[reward type]'}
                </b>{' '}
                when they sign up and spend with AyeFace
              </>
            ) : (
              <>
                Refer your friends &amp; family to earn rewards EACH when they
                sign up and spend with AyeFace
              </>
            )}
          </p>

          <p className="mb-2 text-center text-sm text-gray-500">
            Each successful referral boosts your chances to win an iPhone. Join
            our{' '}
            <a
              href="https://t.me/paywithayeface"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 underline"
            >
              Telegram community
            </a>{' '}
            for all the details.
          </p>

          <Button
            className="w-full max-w-72 sm:max-w-96"
            onClick={handleInviteFriend}
            disabled={
              isLoading || !referralDetails?.success || !isReferralActive
            }
          >
            Invite a friend
          </Button>
        </div>

        <Card className="pt-4 shadow-lg">
          <CardContent className="flex flex-col items-center">
            <h3 className="flex items-center gap-2">
              My referrals
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 cursor-help text-gray-500" />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="w-56 bg-[#F3F3F3]">
                    <p className="text-xs font-extralight text-[#101B2C]">
                      Referral rewards will only be issued after the referrals
                      have completed at least a transaction with AyeFace
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </h3>

            <div className="mt-4 grid w-full grid-cols-2">
              <div className="flex w-full flex-col items-center border-r-1 py-2">
                <h3 className="text-center text-sm">Total Sign Ups</h3>
                <p className="text-sm font-semibold text-primary">
                  {isLoading ? '-' : totalSignUps}
                </p>
              </div>

              <div className="flex flex-col items-center py-2">
                <h3 className="text-center text-sm">Successful Referrals</h3>
                <p className="text-sm font-semibold text-primary">
                  {isLoading ? '-' : successfulReferrals}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {isReferralActive && (
          <ReferralStep
            refAmount={referralDetails?.data?.refAmount}
            refRewardType={referralDetails?.data?.refRewardType}
          />
        )}
      </div>

      <ShareReferralDrawer
        open={openDrawer}
        onOpenChange={setOpenDrawer}
        referralLink={referralLink}
        customMessage={customMessage}
        rewardValue={
          referralDetails?.data?.refereeAmount &&
          referralDetails?.data?.refereeRewardType
            ? `${referralDetails.data.refereeAmount} ${referralDetails.data.refereeRewardType}`
            : undefined
        }
        expiryDate={referralDetails?.data?.referral_period_end}
      />

      <Toaster />
    </>
  );
};

export default Referral;
